// OrganigrammPopup.tsx — Organigramm-Popup für den Unternehmensverbund einer Firma
// (Button „Organigramm" neben „Hinzufügen" bei „Verbindungen zu Firmen").
// Zeigt den transitiven Verbund (Tochterfirma/Schwester/Beteiligung +
// GF/Gesellschafter) in ZWEI eigenständigen Ansichten:
//
//  - „Hierarchie" (Default): klassischer Organigramm-BAUM — Kästchen statt
//    Kreise, Kinder mittig unter ihrem Elternteil, Verbindungen als
//    geschwungene Bögen (Bezier) von der Unterkante des Elternteils zur
//    Oberkante des Kindes; Schwester/Beteiligung als seitliche Querbögen.
//    Kein Ziehen — dafür ist der Baum BEARBEITBAR (mit Schreibrecht): Klick
//    auf ein Kästchen öffnet die Info-Karte mit Stift (Beziehung ändern),
//    Papierkorb (Verbindung entfernen) und „Firma verbinden" (neue Firma an
//    diesen Knoten hängen). Gespeichert wird über die bestehenden
//    contact-relation-Endpoints (inkl. Auto-Schwester-Kaskade im Backend).
//  - „Netzwerk": fluide force-directed Ansicht, Knoten frei ziehbar.
//
// In beiden Ansichten: Klick auf einen Knoten hebt seine Kanten hervor und
// zeigt die Beziehungen in einer Info-Karte (inkl. „Kontakt öffnen"); Pfeil
// zeigt von der Mutter zur Tochter; Schwester gepunktet, Beteiligung
// gestrichelt, Personen-Kanten dünn. Bewusst ohne Graph-Library.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import {
  fetchCompanyNetworkGraph,
  type CompanyNetworkGraph,
  type OrgGraphEdge,
  type OrgGraphNode,
} from "../../services/companyNetworkService";
import { reverseFirmaLabel } from "../../services/haushalteCleanupService";
import { FIRMA_STRUKTUR_TYPEN_SET } from "../../data/firmenverbindungen";
import { istKomplementaerFunktion } from "../../utils/komplementaerGmbh";
import { rollenLabel } from "../verantwortlichkeit/rollenRichtung";
import { anteilAnzeige } from "../../data/anteilsbeziehung";
import { apiFetch } from "../../utils/apiFetch";
import { useBrancheClassification } from "../../data/brancheClassification";
import { brancheIcon, unternehmensfunktionIcon, ICON_VIEWBOX } from "../../data/brancheIcons";
import { branchenAus, funktionenAus } from "../contactRisks/firmenStammdaten";
import { RelationCreateDialog, type EditRelation } from "./RelationCreateDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  ZOOM_MAX,
  ZOOM_MIN,
  eingepassteKamera,
  kameraBegrenzen,
  panelZuWelt,
  radFaktor,
  rahmenUm,
  startKamera,
  verschieben,
  viewBoxVon,
  zoomStufe,
  zoomen,
  type Groesse,
  type Kamera,
  type Punkt,
  type Rahmen,
} from "./organigrammKamera";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

// Logische Zeichenfläche der Netzwerk-Ansicht; die Baum-Ansicht berechnet ihre
// viewBox aus dem Layout (breiteste Zeile, Ebenenzahl).
const NET_W = 1000;
const NET_H = 640;
/** Panelgröße, solange noch nicht gemessen (erster Render, Tests ohne Layout). */
const PANEL_ERSATZ: Groesse = { w: 1000, h: 600 };

// Nur GESPEICHERTE Firma↔Firma-Typen (kein „Mutterfirma" — das ist ein
// Anzeige-Label und würde edgeLabelFor die Inverse zweimal bilden lassen).
// Seit 09/2026 zählt „Joint Venture" mit: Ohne sie wurde eine solche Kante wie
// eine Personen-Rollenkante gezeichnet, zählte nicht in `strukturGrad` und war
// im Bearbeiten-Menü nicht als Firmenverbindung erreichbar.
const COMPANY_EDGE_TYPES: ReadonlySet<string> = FIRMA_STRUKTUR_TYPEN_SET;
/** Stabile leere Menge — verhindert eine neue Referenz je Render. */
const LEERE_IDS: ReadonlySet<number> = new Set<number>();
const GF_TYPES = new Set(["Geschäftsführer", "Gesellschafter"]);

type OrgMode = "hierarchie" | "netzwerk";

/** localStorage-Schlüssel des Schalters „Personen anzeigen" (gilt für alle Firmen). */
export const PERSONEN_ZEIGEN_KEY = "kmvs.organigramm.personenZeigen";

function personenZeigenLesen(): boolean {
  try {
    return window.localStorage.getItem(PERSONEN_ZEIGEN_KEY) !== "0";
  } catch {
    return true;
  }
}

function personenZeigenMerken(v: boolean): void {
  try {
    window.localStorage.setItem(PERSONEN_ZEIGEN_KEY, v ? "1" : "0");
  } catch {
    /* privates Fenster / Speicher gesperrt — dann eben nur für diese Sitzung */
  }
}

type SimNode = OrgGraphNode & {
  x: number;
  y: number;
  vx: number;
  vy: number;
  pinned: boolean;
};

const nodeRadius = (n: OrgGraphNode) => (n.isSelf ? 30 : n.typ === "Firma" ? 25 : 20);
const nodeFill = (n: OrgGraphNode) =>
  n.isSelf ? "#7d2169" : n.typ === "Firma" ? "#a2586a" : "#6e6270";

/**
 * Rolle einer Personen-Kante (aus Sicht „Person ist … der Firma").
 *
 * Die Regel selbst steht seit 20.08.2026 in verantwortlichkeit/rollenRichtung.ts
 * — die Firmen-Akte beschriftet ihre Rollenkanten jetzt genauso, und beide
 * dürfen nicht auseinanderlaufen.
 */
const personRoleLabel = (e: OrgGraphEdge): string => rollenLabel(e.relationType, e.function);

/**
 * Beziehungs-Label aus Sicht des Knotens `nodeId`: was IST der andere Knoten
 * für ihn. Firma↔Firma-Strukturkanten: gespeichertes Label gilt aus Sicht von
 * src („dest ist <label> von src") → auf der dest-Seite die Inverse
 * (Tochterfirma↔Mutterfirma). Rollen-Kanten (GF/Gesellschafter/…): dest ist
 * der Rolleninhaber bei src — ist der AUSGEWÄHLTE Knoten der Rolleninhaber,
 * wird das als „dort <Rolle>" formuliert („BS I GmbH — dort Geschäftsführer").
 */
function edgeLabelFor(nodeId: number, e: OrgGraphEdge): string {
  if (e.relationType === PERSONEN_BRUECKE_EDGE) return e.function ?? "über gemeinsame Person";
  if (!COMPANY_EDGE_TYPES.has(e.relationType)) {
    return nodeId === e.destId ? `dort ${personRoleLabel(e)}` : personRoleLabel(e);
  }
  if (nodeId === e.srcId) return e.relationType;
  return reverseFirmaLabel(e.relationType) ?? e.relationType;
}

function truncate(s: string, max = 30): string {
  return s.length > max ? s.slice(0, max - 1).trimEnd() + "…" : s;
}

/**
 * Firmen-Strukturkanten (Tochter/Schwester/Beteiligung) eines Knotens zählen.
 * Entscheidet, ob eine Komplementärin eigene Substanz hat.
 */
const strukturGrad = (g: CompanyNetworkGraph, id: number): number =>
  g.edges.filter(
    (e) => COMPANY_EDGE_TYPES.has(e.relationType) && (e.srcId === id || e.destId === id),
  ).length;

/**
 * Der Graph ohne die reinen Komplementär-Gesellschaften.
 *
 * Bei einer GmbH & Co. KG ist die Komplementär-GmbH ein Konstrukt des
 * Haftungsrechts: Sie führt die Geschäfte der KG und haftet, hat aber weder
 * eigenes Vermögen noch eigenen Betrieb — bei ihr ist nichts zu versichern und
 * nichts zu beraten. Im Organigramm ist sie ein Kästchen, das die Aufmerksamkeit
 * von der KG wegzieht, in der das Unternehmen tatsächlich steckt.
 *
 * Erkannt wird sie an der GEPFLEGTEN ROLLE („Komplementär" / „Komplementärin
 * (Gesellschaft)"), nicht am Namen: „… Management GmbH" und „…
 * Verwaltungs GmbH" heißen auch Holdings und Besitzgesellschaften, die
 * unbedingt ins Bild gehören. Eine Namensheuristik blendete die falschen aus,
 * und ein fehlendes Kästchen fällt niemandem auf.
 *
 * Zwei Sicherungen, damit nur wegfällt, was wirklich nur Komplementärin ist:
 * die Ausgangsfirma bleibt IMMER stehen (sonst zeigte ihr eigenes Organigramm
 * sie nicht), und eine Komplementärin mit eigenen Tochterfirmen oder
 * Beteiligungen bleibt ebenfalls — sie ist dann zugleich Holding.
 */
export function ohneKomplementaerin(
  g: CompanyNetworkGraph,
  selfId?: number,
): CompanyNetworkGraph {
  const firmen = new Set(g.nodes.filter((n) => n.typ === "Firma").map((n) => n.id));
  const raus = new Set<number>();
  for (const e of g.edges) {
    if (!istKomplementaerFunktion(`${e.relationType ?? ""} ${e.function ?? ""}`)) continue;
    // Rollenkanten kommen normalisiert als src = Gesellschaft, dest = Rolleninhaber.
    // Der Rolleninhaber ist hier die Komplementärin.
    if (!firmen.has(e.srcId) || !firmen.has(e.destId)) continue;
    if (e.destId === selfId) continue;
    if (strukturGrad(g, e.destId) > 0) continue;
    raus.add(e.destId);
  }
  if (raus.size === 0) return g;
  return {
    nodes: g.nodes.filter((n) => !raus.has(n.id)),
    edges: g.edges.filter((e) => !raus.has(e.srcId) && !raus.has(e.destId)),
  };
}

/**
 * Der Graph ohne die Rollen, die für die Struktur nichts aussagen.
 *
 * Übrig bleiben Leitung und Gesellschafter — genau die beiden Kategorien, die
 * das Backend als Organrollen führt (`ORGAN_BEZIEHUNGSTYPEN`: Geschäftsführer →
 * LEITUNG, Gesellschafter → GESELLSCHAFTER). Ansprechpartner, Sachbearbeiter
 * für Verträge, Mitarbeiter und die Kanten ganz ohne Rolle fallen weg: Sie
 * sagen nichts darüber, wem das Unternehmen gehört und wer es führt, füllen
 * aber die Fläche und kreuzen die Bögen, die es sagen.
 *
 * Eine Person, von der danach keine Kante übrig ist, verschwindet mit ihr —
 * sonst stünden ein paar lose Kästchen am Rand, deren Bezug zum Bild nicht mehr
 * zu erkennen ist. Firmen bleiben stehen: ihre Zugehörigkeit steht in den
 * Strukturkanten, nicht in einer Rolle.
 */
export function nurOrganRollen(g: CompanyNetworkGraph): CompanyNetworkGraph {
  const edges = g.edges.filter(
    (e) => COMPANY_EDGE_TYPES.has(e.relationType) || GF_TYPES.has(e.relationType),
  );
  const verbunden = new Set<number>();
  for (const e of edges) {
    verbunden.add(e.srcId);
    verbunden.add(e.destId);
  }
  const nodes = g.nodes.filter((n) => n.typ === "Firma" || verbunden.has(n.id));
  const ids = new Set(nodes.map((n) => n.id));
  return { nodes, edges: edges.filter((e) => ids.has(e.srcId) && ids.has(e.destId)) };
}

/**
 * Der Graph ohne Personen — für die Verbund-Ansicht des Risiken-Reiters.
 *
 * Mit den Personen fallen ALLE Kanten weg, die an einer Person hängen. Bliebe
 * auch nur eine übrig, zeichnete der Baum einen Bogen zu einem Knoten, den es
 * nicht mehr gibt: Positionen werden über die Knoten-id gesucht, eine fehlende
 * Position lässt den Bogen ins Leere laufen.
 *
 * Nebenwirkung, die hier erwünscht ist: Geschäftsführer und Gesellschafter
 * sitzen im Baum ÜBER ihrer Firma (sie sind deren Eltern). Ohne sie steht oben,
 * wonach im Verbund gesucht wird — die Mutter- bzw. Holdinggesellschaft.
 *
 * Firmen, von denen danach KEINE Kante übrig ist, fallen mit weg. Sie hängen
 * nur über einen gemeinsamen Menschen im Verbund — im Bild ohne Menschen
 * stehen sie als Kästchen ohne jede Linie da und behaupten eine Zugehörigkeit,
 * die sie nicht zeigen. (Früher blieben sie stehen, „als Firma des Verbunds";
 * in der Praxis war das die Komplementär-GmbH und die Firma des Steuerberaters,
 * die beide oben in der Wurzelzeile Platz nahmen und die Struktur
 * auseinanderzogen.) Wer sie sucht, findet sie im Organigramm MIT Personen —
 * dort ist auch zu sehen, worüber sie verbunden sind.
 *
 * Die Ausgangsfirma bleibt immer stehen: Ihr eigenes Organigramm ohne sie wäre
 * leer.
 *
 * Mit `brueckenKanten` (Schalter „Personen anzeigen" aus) gilt das Wegfallen
 * NICHT für Firmen, die über eine gemeinsame Person hängen: Statt Firma →
 * Person → Firma steht dann eine gestrichelte Brücke „über <Name>" da, und die
 * Firma bleibt mit ihr im Bild. Siehe `personenBruecken`.
 */
export function nurFirmenGraph(
  g: CompanyNetworkGraph,
  selfId?: number,
  opts: { brueckenKanten?: boolean } = {},
): CompanyNetworkGraph {
  const firmen = g.nodes.filter((n) => n.typ === "Firma");
  const firmenIds = new Set(firmen.map((n) => n.id));
  const edges = g.edges.filter((e) => firmenIds.has(e.srcId) && firmenIds.has(e.destId));
  if (opts.brueckenKanten) edges.push(...personenBruecken(g, firmenIds, edges));
  const verbunden = new Set<number>();
  for (const e of edges) {
    verbunden.add(e.srcId);
    verbunden.add(e.destId);
  }
  const nodes = firmen.filter((n) => n.isSelf || n.id === selfId || verbunden.has(n.id));
  // Alle behalten, wenn NICHTS verbunden ist: eine Einzelfirma ohne Verbund
  // soll ihr eigenes Kästchen sehen, nicht eine leere Fläche.
  if (nodes.length === 0) return { nodes: firmen, edges };
  const ids = new Set(nodes.map((n) => n.id));
  return { nodes, edges: edges.filter((e) => ids.has(e.srcId) && ids.has(e.destId)) };
}

/**
 * Beziehungstyp der gerechneten Kante „zwei Firmen teilen sich eine Person".
 *
 * KEIN Wert aus `ContactRelationType` — die Kante steht nicht in der Datenbank,
 * sondern ersetzt im Organigramm OHNE Personen (Schalter „Personen anzeigen")
 * den Weg Firma → Person → Firma. Die Namen der Personen stehen in `function`.
 */
export const PERSONEN_BRUECKE_EDGE = "Personenbrücke";

/**
 * Ids der Brückenkanten: negativ (gerechnet, nicht gespeichert — kein PATCH
 * und kein DELETE kann damit eine echte ContactRelation treffen) und in einem
 * eigenen Bereich, damit sie nie mit den Verwaltungs-Kanten (-1, -2, …)
 * als React-key kollidieren.
 */
const BRUECKE_ID_START = -1_000_000;

/**
 * Brückenkanten für das Organigramm ohne Personen.
 *
 * WARUM: Das Backend zieht Firmen auch dann in den Verbund, wenn sie nur über
 * einen gemeinsamen Menschen (GF, Gesellschafter) mit ihm verbunden sind. Blendet
 * man die Personen aus, stünden diese Firmen ohne jede Linie da — oder fielen
 * ganz weg. Die Brücke zeigt stattdessen „A hängt über Frau Meier an B".
 *
 * Je Firmenpaar genau EINE Brücke; teilen sich zwei Firmen mehrere Personen,
 * stehen alle Namen im Label. Haben die beiden schon eine echte Firmenkante,
 * entfällt die Brücke — die gespeicherte Beziehung sagt mehr.
 */
function personenBruecken(
  g: CompanyNetworkGraph,
  firmenIds: ReadonlySet<number>,
  firmenKanten: OrgGraphEdge[],
): OrgGraphEdge[] {
  const personen = new Map(
    g.nodes.filter((n) => n.typ === "Person").map((n) => [n.id, n.name || "—"]),
  );
  // Personen-Kanten kommen normalisiert (src = Firma, dest = Person) — beide
  // Richtungen gelesen, damit ein Ausreißer nicht still eine Brücke verliert.
  const firmenJePerson = new Map<number, Set<number>>();
  for (const e of g.edges) {
    const [firma, person] = firmenIds.has(e.srcId) && personen.has(e.destId)
      ? [e.srcId, e.destId]
      : firmenIds.has(e.destId) && personen.has(e.srcId)
        ? [e.destId, e.srcId]
        : [null, null];
    if (firma == null || person == null) continue;
    const set = firmenJePerson.get(person) ?? new Set<number>();
    set.add(firma);
    firmenJePerson.set(person, set);
  }

  const paarKey = (a: number, b: number) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const direkt = new Set(firmenKanten.map((e) => paarKey(e.srcId, e.destId)));
  const namenJePaar = new Map<string, { a: number; b: number; namen: string[] }>();
  for (const [personId, firmenSet] of firmenJePerson) {
    const liste = [...firmenSet].sort((x, y) => x - y);
    for (let i = 0; i < liste.length; i++) {
      for (let j = i + 1; j < liste.length; j++) {
        const key = paarKey(liste[i], liste[j]);
        if (direkt.has(key)) continue;
        const eintrag = namenJePaar.get(key) ?? { a: liste[i], b: liste[j], namen: [] };
        const name = personen.get(personId)!;
        if (!eintrag.namen.includes(name)) eintrag.namen.push(name);
        namenJePaar.set(key, eintrag);
      }
    }
  }

  let id = BRUECKE_ID_START;
  return [...namenJePaar.values()].map(({ a, b, namen }) => ({
    id: id--,
    srcId: a,
    destId: b,
    relationType: PERSONEN_BRUECKE_EDGE,
    share: null,
    function: `über ${namen.sort((x, y) => x.localeCompare(y, "de")).join(", ")}`,
  }));
}

/**
 * Beziehungstyp der eingeblendeten Verwaltungs-Kante.
 *
 * KEIN Wert aus `ContactRelationType`: Beauftragt wird die Verwaltung je
 * Gebäude (Merkmal `hvKontaktRef` am Risikoobjekt), nicht als Beziehung
 * zwischen zwei Kontakten. Der Graph bekommt sie deshalb hier dazugerechnet,
 * die Datenbank kennt sie an dieser Stelle nicht.
 */
export const VERWALTUNG_EDGE = "Hausverwaltung";

/** Eine am Gebäude beauftragte Verwaltung, aufgelöst auf einen echten Kontakt. */
export type Verwaltung = { contactId: number; name: string };

/**
 * Ergänzt den Firmen-Graphen um die Hausverwaltungen der Gebäude.
 *
 * WARUM: Im Immobilienverbund ist die Verwaltung die Stelle, an der die
 * Objekte tatsächlich betreut werden — wer den Verbund ansieht, um Risiken
 * einzuordnen, muss sie sehen. Sie steht aber nicht zwingend im Verbund: Sie
 * kann eine Schwestergesellschaft sein (dann ist sie schon im Bild und wird
 * nur markiert) oder ein fremder Dienstleister (dann kommt sie als eigenes
 * Kästchen dazu, angebunden an die betrachtete Firma).
 *
 * Nur Verwaltungen MIT Kontaktbezug: Ein Kästchen ohne dahinterliegenden
 * Kontakt ließe sich nicht anklicken und führte nirgendwohin — der bloße Name
 * steht in der Immobilienmappe.
 */
export function mitVerwaltungen(
  g: CompanyNetworkGraph,
  verwaltungen: Verwaltung[],
  ankerId: number,
): { graph: CompanyNetworkGraph; verwalterIds: Set<number> } {
  const verwalterIds = new Set<number>();
  if (!verwaltungen.length) return { graph: g, verwalterIds };

  const vorhanden = new Set(g.nodes.map((n) => n.id));
  const nodes = [...g.nodes];
  const edges = [...g.edges];
  // Negative ids: die Kante ist gerechnet, nicht gespeichert. Der Stift der
  // Info-Karte hängt an `COMPANY_EDGE_TYPES` und greift hier ohnehin nicht —
  // die negative id macht zusätzlich unmöglich, dass ein PATCH eine echte
  // ContactRelation trifft.
  let kantenId = -1;
  for (const v of verwaltungen) {
    if (!Number.isFinite(v.contactId) || v.contactId <= 0) continue;
    if (v.contactId === ankerId) continue; // verwaltet sich selbst — keine Kante
    if (verwalterIds.has(v.contactId)) continue;
    verwalterIds.add(v.contactId);
    if (!vorhanden.has(v.contactId)) {
      vorhanden.add(v.contactId);
      nodes.push({ id: v.contactId, name: v.name, typ: "Firma", isSelf: false });
      edges.push({
        id: kantenId--,
        srcId: ankerId,
        destId: v.contactId,
        relationType: VERWALTUNG_EDGE,
        share: null,
        function: null,
      });
    }
  }
  return { graph: { nodes, edges }, verwalterIds };
}

// ─── Baum-Layout (Hierarchie-Ansicht) ────────────────────────────────────────

// Kästchen-Maße + Abstände des Baums.
// Breit genug für einen Firmennamen mit Rechtsform. Bei 176 px passten 19
// Zeichen — „Caspar u. Co. GmbH & Co. KG" brach schon nach „Caspar u. Co. GmbH…"
// ab, und damit fiel weg, was die Firma von ihrer Schwester unterscheidet.
export const BOX_FIRMA_W = 240;
export const BOX_FIRMA_H = 46;
const BOX_PERSON_W = 210;
const BOX_PERSON_H = 40;
export const SIB_GAP = 26; // horizontaler Abstand zwischen Geschwister-Subbäumen
const ROW_H = 112; // vertikaler Ebenen-Abstand (Boxhöhe + Raum für die Bögen)
const ROOT_ROW_GAP = 48; // Abstand zwischen umgebrochenen Wurzel-Zeilen
const MAX_ROOT_ROW_W = 1500; // ab hier brechen weitere Wurzel-Bäume in die nächste Zeile um

// Verbund-Ansicht (Risiken-Reiter): das Kästchen trägt vier Zeilen statt einer
// — Name, Anschrift, Funktion, Branche, Tätigkeit — und braucht dafür Platz.
export const BOX_DETAIL_W = 268;
export const BOX_DETAIL_H = 116;

const boxW = (n: OrgGraphNode, detail = false) =>
  n.typ === "Firma" ? (detail ? BOX_DETAIL_W : BOX_FIRMA_W) : BOX_PERSON_W;
const boxH = (n: OrgGraphNode, detail = false) =>
  n.typ === "Firma" ? (detail ? BOX_DETAIL_H : BOX_FIRMA_H) : BOX_PERSON_H;

type TreeLayout = {
  /** Kästchen-MITTELPUNKTE je Knoten-id. */
  pos: Map<number, { x: number; y: number }>;
  /** Primär-Elternteil je Kind (Baum-Kante). */
  primary: Map<number, { p: number; e: OrgGraphEdge }>;
  /** Alle übrigen Kanten (Schwester/Beteiligung, Zweit-Eltern) als Querbögen. */
  cross: OrgGraphEdge[];
  w: number;
  h: number;
};

/**
 * Klassischer Organigramm-Baum: Eltern→Kind wie in der Ebenen-Logik zuvor
 * (Tochterfirma: Mutter über Tochter; GF/Gesellschafter: Rolleninhaber über
 * der Firma; sonstige Rollen: Person unter ihre Firma). Aus dem Kanten-DAG
 * wird ein Wald: jedes Kind hängt unter genau EINEM Primär-Elternteil (dem
 * tiefsten; Tochterfirma-Kanten bevorzugt), alle übrigen Kanten werden zu
 * Querbögen. Kinder werden mittig unter ihrem Elternteil zentriert
 * (Subbaum-Breiten rekursiv); breite Wurzel-Wälder brechen in Zeilen um.
 */
export function treeLayout(
  nodes: OrgGraphNode[],
  edges: OrgGraphEdge[],
  detail = false,
): TreeLayout {
  const byId = new Map(nodes.map((n) => [n.id, n]));

  const cands = new Map<number, { p: number; e: OrgGraphEdge }[]>();
  const addCand = (p: number, c: number, e: OrgGraphEdge) => {
    if (p === c || !byId.has(p) || !byId.has(c)) return;
    const list = cands.get(c) ?? [];
    if (!list.some((x) => x.p === p)) {
      list.push({ p, e });
      cands.set(c, list);
    }
  };
  for (const e of edges) {
    // Brückenkanten (gemeinsame Person) sind keine Über-/Unterordnung — sie
    // laufen wie „Schwester" als Querbogen und können so keinen Zyklus bilden.
    if (e.relationType === PERSONEN_BRUECKE_EDGE) continue;
    if (e.relationType === "Tochterfirma") {
      addCand(e.srcId, e.destId, e);
    } else if (GF_TYPES.has(e.relationType)) {
      addCand(e.destId, e.srcId, e);
    } else if (!COMPANY_EDGE_TYPES.has(e.relationType)) {
      // Sonstige Rollen (Verantwortliche/Mitarbeiter): Person unter die Firma
      // (Personen-Kanten kommen normalisiert als src=Firma → dest=Person).
      addCand(e.srcId, e.destId, e);
    }
  }

  // Tiefe = längster Pfad von den Wurzeln; Zyklen über den Rekursionsstack kappen.
  const depthMemo = new Map<number, number>();
  const inStack = new Set<number>();
  const depthOf = (id: number): number => {
    const memo = depthMemo.get(id);
    if (memo != null) return memo;
    if (inStack.has(id)) return 0;
    inStack.add(id);
    let d = 0;
    for (const c of cands.get(id) ?? []) d = Math.max(d, depthOf(c.p) + 1);
    inStack.delete(id);
    depthMemo.set(id, d);
    return d;
  };
  nodes.forEach((n) => depthOf(n.id));

  // Primär-Elternteil: der TIEFSTE Kandidat (Tochterfirma bevorzugt). Nur echt
  // flachere Eltern zulassen — so bleibt der Wald auch bei Zyklen
  // (Kreuzbeteiligung A⇄B) garantiert azyklisch.
  const primary = new Map<number, { p: number; e: OrgGraphEdge }>();
  for (const [c, list] of cands) {
    const dc = depthOf(c);
    const ok = list.filter((x) => depthOf(x.p) < dc);
    if (!ok.length) continue;
    ok.sort(
      (a, b) =>
        depthOf(b.p) - depthOf(a.p) ||
        Number(b.e.relationType === "Tochterfirma") - Number(a.e.relationType === "Tochterfirma") ||
        a.p - b.p,
    );
    primary.set(c, ok[0]);
  }

  const children = new Map<number, OrgGraphNode[]>();
  for (const n of nodes) {
    const pr = primary.get(n.id);
    if (!pr) continue;
    const list = children.get(pr.p) ?? [];
    list.push(n);
    children.set(pr.p, list);
  }
  const nodeOrder = (a: OrgGraphNode, b: OrgGraphNode) =>
    a.typ === b.typ
      ? (a.name ?? "").localeCompare(b.name ?? "", "de")
      : a.typ === "Firma"
        ? -1
        : 1;
  for (const list of children.values()) list.sort(nodeOrder);

  // Subbaum-Breite (px) und -Höhe (Ebenen), rekursiv mit Memo.
  const widthMemo = new Map<number, number>();
  const widthOf = (id: number): number => {
    const memo = widthMemo.get(id);
    if (memo != null) return memo;
    const own = boxW(byId.get(id)!, detail) + SIB_GAP;
    const kids = children.get(id) ?? [];
    const w = Math.max(own, kids.reduce((acc, k) => acc + widthOf(k.id), 0));
    widthMemo.set(id, w);
    return w;
  };
  const levelsMemo = new Map<number, number>();
  const levelsOf = (id: number): number => {
    const memo = levelsMemo.get(id);
    if (memo != null) return memo;
    const kids = children.get(id) ?? [];
    const l = 1 + (kids.length ? Math.max(...kids.map((k) => levelsOf(k.id))) : 0);
    levelsMemo.set(id, l);
    return l;
  };

  // Wurzeln: Baum mit der Ausgangsfirma zuerst, dann Firmen vor Personen.
  const roots = nodes.filter((n) => !primary.has(n.id));
  const hasSelfMemo = new Map<number, boolean>();
  const hasSelf = (id: number): boolean => {
    const memo = hasSelfMemo.get(id);
    if (memo != null) return memo;
    const v = !!byId.get(id)?.isSelf || (children.get(id) ?? []).some((k) => hasSelf(k.id));
    hasSelfMemo.set(id, v);
    return v;
  };
  roots.sort((a, b) => Number(hasSelf(b.id)) - Number(hasSelf(a.id)) || nodeOrder(a, b));

  /** Alle über eine Kante erreichbaren Nachbarn eines Knotens. */
  const nachbarn = (id: number): number[] =>
    edges
      .filter((e) => e.srcId === id || e.destId === id)
      .map((e) => (e.srcId === id ? e.destId : e.srcId))
      .filter((other) => other !== id && byId.has(other));

  /**
   * Satelliten: Wurzeln ohne eigene Kinder, deren einzige Verbindung zu einem
   * Knoten TIEFER im Baum führt — ein Zweit-Gesellschafter neben der Mutter,
   * ein Geschäftsführer, dessen Firma schon unter jemand anderem hängt.
   *
   * In der Wurzelzeile landeten sie ganz außen, auf der Ebene der
   * Holdinggesellschaften und quer über das halbe Bild von der Firma entfernt,
   * zu der sie gehören. Sie stehen deshalb nicht dort, sondern EINE EBENE ÜBER
   * ihrem Anker — da, wo ein zweiter Gesellschafter hingehört.
   *
   * Die Bedingung „Anker ist selbst keine Wurzel" ist zugleich die Garantie,
   * dass die Ebene darüber existiert: Ein Knoten mit Elternteil sitzt
   * mindestens auf Ebene 1.
   */
  const istWurzel = new Set(roots.map((r) => r.id));
  const satelliten = roots.filter(
    (r) =>
      (children.get(r.id) ?? []).length === 0 &&
      nachbarn(r.id).some((o) => !istWurzel.has(o)),
  );
  const satIds = new Set(satelliten.map((n) => n.id));
  const echteWurzeln = roots.filter((r) => !satIds.has(r.id));

  // Wurzel-Bäume in Zeilen umbrechen — ein flacher 30-Firmen-Schwester-Cluster
  // würde sonst eine einzige, winzig skalierte Zeile ergeben.
  const rootRows: OrgGraphNode[][] = [];
  let row: OrgGraphNode[] = [];
  let rowW = 0;
  for (const r of echteWurzeln) {
    const w = widthOf(r.id);
    if (row.length && rowW + w > MAX_ROOT_ROW_W) {
      rootRows.push(row);
      row = [];
      rowW = 0;
    }
    row.push(r);
    rowW += w;
  }
  if (row.length) rootRows.push(row);

  const pos = new Map<number, { x: number; y: number }>();
  const place = (id: number, left: number, y: number) => {
    const w = widthOf(id);
    pos.set(id, { x: left + w / 2, y });
    const kids = children.get(id) ?? [];
    let childLeft = left + (w - kids.reduce((a, k) => a + widthOf(k.id), 0)) / 2;
    for (const k of kids) {
      place(k.id, childLeft, y + rowH);
      childLeft += widthOf(k.id);
    }
  };

  // Ebenenabstand = Kästchenhöhe + Luft für die Bögen. In der Detail-Ansicht
  // sind die Kästchen mehr als doppelt so hoch; mit dem festen ROW_H würden
  // Eltern und Kind einander überlappen.
  const rowH = detail ? BOX_DETAIL_H + (ROW_H - BOX_FIRMA_H) : ROW_H;
  const totalW0 = Math.max(
    NET_W,
    ...rootRows.map((r) => r.reduce((a, n) => a + widthOf(n.id), 0) + 80),
    0,
  );
  let y = 72;
  for (const r of rootRows) {
    const rw = r.reduce((a, n) => a + widthOf(n.id), 0);
    let left = (totalW0 - rw) / 2;
    for (const n of r) {
      place(n.id, left, y);
      left += widthOf(n.id);
    }
    y += Math.max(...r.map((n) => levelsOf(n.id))) * rowH + ROOT_ROW_GAP;
  }
  // ── Satelliten: eine Ebene über ihren Anker, waagerecht daneben ──────────
  // Belegte Rechtecke der bereits platzierten Knoten — der Satellit sucht sich
  // von seiner Wunschposition aus nach links und rechts die erste Lücke.
  const belegt = Array.from(pos, ([id, p]) => ({
    x: p.x,
    y: p.y,
    w: boxW(byId.get(id)!, detail),
    h: boxH(byId.get(id)!, detail),
  }));
  const frei = (x: number, y: number, w: number, hh: number) =>
    !belegt.some(
      (b) => Math.abs(b.x - x) < (b.w + w) / 2 + SIB_GAP && Math.abs(b.y - y) < (b.h + hh) / 2 + 8,
    );

  for (const sat of satelliten) {
    const anker = nachbarn(sat.id)
      .map((id) => pos.get(id))
      .filter((p): p is { x: number; y: number } => !!p);
    if (!anker.length) {
      // Kein platzierter Anker (der Anker war selbst ein Satellit) — dann gilt
      // die Wurzelbehandlung weiter: unten links, statt gar nicht gezeichnet.
      pos.set(sat.id, { x: totalW0 / 2, y: 72 });
      continue;
    }
    // Die OBERSTE Ebene unter den Ankern gibt die Zeile vor; auf ihr liegende
    // Anker geben die Wunsch-Spalte. Bei zwei Kindern auf derselben Ebene
    // (Gesellschafter zweier Schwestern) steht der Satellit mittig über beiden.
    const yKind = Math.min(...anker.map((a) => a.y));
    const aufEbene = anker.filter((a) => a.y === yKind);
    const xWunsch = aufEbene.reduce((acc, a) => acc + a.x, 0) / aufEbene.length;
    const yy = yKind - rowH;
    const w = boxW(sat, detail);
    const hh = boxH(sat, detail);
    let x = xWunsch;
    for (let i = 1; !frei(x, yy, w, hh) && i <= 400; i++) {
      // abwechselnd nach links und rechts, in halben Kästchenbreiten
      const schritt = Math.ceil(i / 2) * ((w + SIB_GAP) / 2);
      x = xWunsch + (i % 2 === 1 ? -schritt : schritt);
    }
    pos.set(sat.id, { x, y: yy });
    belegt.push({ x, y: yy, w, h: hh });
  }

  // Ein nach links ausgewichener Satellit kann über den linken Rand geraten —
  // dann wandert das ganze Bild nach rechts statt ihn abzuschneiden.
  let minX = Infinity;
  let maxX = -Infinity;
  for (const [id, p] of pos) {
    const halb = boxW(byId.get(id)!, detail) / 2 + 40;
    minX = Math.min(minX, p.x - halb);
    maxX = Math.max(maxX, p.x + halb);
  }
  if (Number.isFinite(minX) && minX < 0) {
    for (const p of pos.values()) p.x -= minX;
    maxX -= minX;
  }
  const totalW = Math.max(NET_W, Number.isFinite(maxX) ? maxX : 0);

  const h = Math.max(
    NET_H,
    y - ROOT_ROW_GAP - rowH + (detail ? BOX_DETAIL_H : BOX_FIRMA_H) / 2 + 40,
  );

  const treeEdgeIds = new Set(Array.from(primary.values(), (x) => x.e.id));
  const cross = edges.filter(
    (e) => !treeEdgeIds.has(e.id) && byId.has(e.srcId) && byId.has(e.destId),
  );

  return { pos, primary, cross, w: totalW, h };
}

/**
 * Quote einer Kante. Hat das Backend zwei Zeilen derselben Beteiligung zu
 * einer zusammengefasst (Tochterfirma + Gesellschafter) und tragen sie
 * verschiedene Quoten, stehen beide da — markiert statt still entschieden.
 */
const kantenAnteil = (e: OrgGraphEdge): string => anteilAnzeige(e.share, e.anteilWiderspruch);

/** Label einer Baum-Kante (steht dauerhaft am Bogen). */
function treeEdgeLabel(e: OrgGraphEdge): string {
  if (e.relationType === VERWALTUNG_EDGE) return "verwaltet";
  if (e.relationType === PERSONEN_BRUECKE_EDGE) return e.function ?? "";
  if (e.relationType === "Tochterfirma") return kantenAnteil(e) ? `Anteile ${kantenAnteil(e)}` : "";
  if (COMPANY_EDGE_TYPES.has(e.relationType)) return e.relationType;
  return personRoleLabel(e) + (kantenAnteil(e) ? ` · ${kantenAnteil(e)}` : "");
}

/** Label einer hervorgehobenen Kante (Querbogen bzw. Netzwerk-Linie). */
function hoverLabel(e: OrgGraphEdge): string {
  if (e.relationType === PERSONEN_BRUECKE_EDGE) return e.function ?? "";
  const anteil = kantenAnteil(e) ? ` · ${kantenAnteil(e)}` : "";
  return (COMPANY_EDGE_TYPES.has(e.relationType) ? e.relationType : personRoleLabel(e)) + anteil;
}

/** Ankerpunkt auf dem Kästchen-Rand in Richtung des Gegenknotens (Querbögen). */
function rectAnchor(
  from: { x: number; y: number },
  node: OrgGraphNode,
  to: { x: number; y: number },
  detail = false,
): { x: number; y: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const hw = boxW(node, detail) / 2 + 4;
  const hh = boxH(node, detail) / 2 + 4;
  const s = Math.min(
    dx ? Math.abs(hw / dx) : Infinity,
    dy ? Math.abs(hh / dy) : Infinity,
    0.5,
  );
  return { x: from.x + dx * s, y: from.y + dy * s };
}

const BuildingGlyph = (
  <g stroke="#fff" strokeWidth={1.6} fill="none" strokeLinecap="round">
    <rect x={-7} y={-9} width={14} height={18} rx={1.5} />
    <path d="M-3.4 -5h2.2M1.2 -5h2.2M-3.4 -1h2.2M1.2 -1h2.2M-3.4 3h2.2M1.2 3h2.2" />
  </g>
);
const PersonGlyph = (
  <g stroke="#fff" strokeWidth={1.8} fill="none" strokeLinecap="round">
    <circle cx={0} cy={-3.6} r={3.4} />
    <path d="M-6 7.4c.9-4.1 3.1-6 6-6s5.1 1.9 6 6" />
  </g>
);
// Zeilen-Aktionen der Info-Karte (wie in ContactConnections, kompakter).
const PenIcon = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 4.5l2 2M4 16l1-3.5 8-8a1.4 1.4 0 012 2l-8 8L4 16z" /></svg>
);
const DelIcon = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h12M8 6V4.5A1.5 1.5 0 019.5 3h1A1.5 1.5 0 0112 4.5V6M6 6l.7 9a1.5 1.5 0 001.5 1.4h3.6a1.5 1.5 0 001.5-1.4L15 6M8.5 9v4M11.5 9v4" /></svg>
);

export function OrganigrammPopup({
  companyId,
  companyName,
  canWrite = false,
  variant = "popup",
  verbundAnsicht = false,
  verwaltungen,
  onChanged,
  onClose,
  vorgabeGraph,
}: {
  companyId: number;
  companyName?: string;
  /** Schreibrecht des Users auf den Ausgangskontakt — schaltet das Bearbeiten frei. */
  canWrite?: boolean;
  /**
   * "popup" (Default): modales Overlay mit Schließen-Button und Escape.
   * "inline": derselbe Graph ohne Overlay/Escape/X, eingebettet in eine Karte
   * (Risiken-Reiter). Die Bearbeiten-Dialoge funktionieren in beiden Varianten.
   */
  variant?: "popup" | "inline";
  /**
   * Verbund-Ansicht des Risiken-Reiters: NUR Firmen (keine Personen), und die
   * Kästchen tragen die Stammdaten, auf denen die Risikobetrachtung beruht —
   * Anschrift, Unternehmensfunktion, Branche, Tätigkeit.
   *
   * Ohne Personen steht oben, wonach dort gesucht wird: die Mutter- bzw.
   * Holdinggesellschaft. Mit ihnen säßen die Geschäftsführer über der Firma
   * (sie sind im Baum deren Eltern) und verdeckten genau diese Aussage.
   * Die Netzwerk-Ansicht entfällt hier — mit vier Zeilen je Kästchen ist ein
   * frei schwebender Kräftegraph nicht mehr lesbar.
   */
  verbundAnsicht?: boolean;
  /**
   * Beauftragte Hausverwaltungen der Gebäude (nur Verbund-Ansicht). Sie stehen
   * am Risikoobjekt, nicht an der Kontaktbeziehung — der Aufrufer liest sie
   * aus den Immobilienmappen und reicht sie durch. Siehe `mitVerwaltungen`.
   *
   * Der Aufrufer muss die Liste stabil halten (`useMemo`), sonst rechnet der
   * Graph bei jedem Render neu.
   */
  verwaltungen?: Verwaltung[];
  /** Nach einer gespeicherten Änderung (Detailseite neu laden lassen). */
  onChanged?: () => void;
  /** Nur in der Popup-Variante nötig. */
  onClose?: () => void;
  /**
   * Fertiger Graph statt API — für die Kontaktanlage, in der es den Kontakt
   * noch nicht gibt (User 26.09.2026). Dann wird nichts geladen und nichts
   * bearbeitet; `companyId` ist die id des Selbst-Knotens im Graphen.
   */
  vorgabeGraph?: CompanyNetworkGraph;
}) {
  const isInline = variant === "inline";
  // Branchen-Katalog: löst `brancheClassId` zum Anzeigepfad auf. Der Hook cacht
  // modulweit, der Risiken-Reiter hat ihn in aller Regel schon geladen.
  const { rows: brancheRows } = useBrancheClassification();
  const [graphRoh, setGraph] = React.useState<CompanyNetworkGraph | null>(null);
  /**
   * Der Rohstand, um die reinen Komplementär-Gesellschaften und die Rollen ohne
   * Struktur-Aussage bereinigt — die Grundlage BEIDER Ansichten.
   *
   * Reihenfolge zählt: Die Komplementärin wird an ihrer Rolle erkannt, und
   * genau diese Rolle wirft `nurOrganRollen` weg (sie ist weder Leitung noch
   * Gesellschafter). Andersherum bliebe die Komplementärin für immer im Bild.
   */
  const graphBasis = React.useMemo(
    () => (graphRoh ? nurOrganRollen(ohneKomplementaerin(graphRoh, companyId)) : null),
    [graphRoh, companyId],
  );
  /**
   * Was gezeichnet wird. Der Rohstand bleibt im State, damit ein Umschalten
   * der Ansicht keinen neuen Request braucht.
   */
  const verbund = React.useMemo(
    () =>
      graphBasis && verbundAnsicht
        ? mitVerwaltungen(nurFirmenGraph(graphBasis, companyId), verwaltungen ?? [], companyId)
        : null,
    [graphBasis, verbundAnsicht, verwaltungen, companyId],
  );
  /**
   * Schalter „Personen anzeigen" (beide Ansichten, nicht in der Verbund-Ansicht —
   * die zeigt ohnehin nur Firmen). Personen kommen im Organigramm mehrfach vor
   * und verwirren; ausgeblendet ersetzen Brückenkanten den Weg über sie.
   */
  const [personenZeigenRoh, setPersonenZeigenRoh] = React.useState<boolean>(personenZeigenLesen);
  const personenZeigen = verbundAnsicht || personenZeigenRoh;
  const setPersonenZeigen = React.useCallback((v: boolean) => {
    setPersonenZeigenRoh(v);
    personenZeigenMerken(v);
  }, []);
  const ohnePersonen = React.useMemo(
    () =>
      graphBasis && !personenZeigen
        ? nurFirmenGraph(graphBasis, companyId, { brueckenKanten: true })
        : null,
    [graphBasis, personenZeigen, companyId],
  );
  const graph: CompanyNetworkGraph | null = verbund
    ? verbund.graph
    : ohnePersonen ?? graphBasis;
  /** Firmen, die Gebäude des Verbunds verwalten — ihr Kästchen trägt die Marke. */
  const verwalterIds = verbund?.verwalterIds ?? LEERE_IDS;
  const [error, setError] = React.useState(false);
  const [selected, setSelected] = React.useState<number | null>(null);
  const [modeRoh, setMode] = React.useState<OrgMode>("hierarchie");
  // Fällt der gewählte Knoten aus dem Bild (Personen ausgeblendet), darf keine
  // „Geister-Auswahl" bleiben — sie dimmte alles, ohne dass eine Karte käme.
  React.useEffect(() => {
    if (!graph) return;
    setSelected((prev) =>
      prev != null && !graph.nodes.some((n) => n.id === prev) ? null : prev,
    );
  }, [graph]);
  const mode: OrgMode = verbundAnsicht ? "hierarchie" : modeRoh;
  const [, setTick] = React.useState(0);
  // Zoom/Verschieben (siehe organigrammKamera.ts): 100 % = echte Größe. `null`
  // heißt „noch keine Wahl des Users" — dann gilt die Startansicht.
  const [kameraWahl, setKameraWahl] = React.useState<Kamera | null>(null);
  /** Zählt hoch, wenn ein neuer Graph von außen kommt — dann beginnt die Ansicht neu. */
  const [graphEpoche, setGraphEpoche] = React.useState(0);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const [panelGemessen, setPanelGemessen] = React.useState<Groesse | null>(null);
  // Bearbeiten: RelationCreateDialog (Anlegen ohne `edit`, Ändern mit) aus der
  // Perspektive des Knotens `forId`; ConfirmDialog fürs Entfernen einer Kante.
  const [relDialog, setRelDialog] = React.useState<
    { forId: number; forName: string; edit?: EditRelation } | null
  >(null);
  const [confirmDel, setConfirmDel] = React.useState<
    { edge: OrgGraphEdge; otherName: string } | null
  >(null);
  const [delBusy, setDelBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState("");
  const navigate = useNavigate();

  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const nodesRef = React.useRef<SimNode[]>([]);
  const alphaRef = React.useRef(0);
  const rafRef = React.useRef<number | null>(null);
  // Aktiver Drag: window-Listener-Cleanup, damit ein Unmount mitten im Drag
  // keine verwaisten Listener (und darüber neue rAF-Sim-Schleifen) hinterlässt.
  const dragCleanupRef = React.useRef<(() => void) | null>(null);
  React.useEffect(() => () => dragCleanupRef.current?.(), []);

  // Escape schließt das Popup — aber nicht, solange ein Dialog offen ist
  // (der fängt Escape selbst ab und soll nicht das Organigramm mitreißen).
  React.useEffect(() => {
    // Inline gibt es nichts zu schließen — und ein globaler Escape-Listener
    // würde dort fremde Dialoge stören.
    if (isInline) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !relDialog && !confirmDel) onClose?.();
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [isInline, onClose, relDialog, confirmDel]);

  // Der RelationCreateDialog portalt mit z-index 1400 nach body — das Overlay
  // liegt auf 10000. Solange er offen ist, hebt eine body-Klasse die Scrims an.
  React.useEffect(() => {
    if (!relDialog) return;
    document.body.classList.add("org-dlg-open");
    return () => document.body.classList.remove("org-dlg-open");
  }, [relDialog]);

  // Sequenz-Guard über ALLE Graph-Fetches (Mount-Load + refresh): es zählt nur
  // die Antwort des jeweils letzten Requests. Ohne das könnte eine langsame
  // frühere Antwort einen späteren Stand überschreiben — eine gerade entfernte
  // Verbindung erschiene wieder im Baum.
  const fetchSeqRef = React.useRef(0);

  // Graph laden.
  React.useEffect(() => {
    if (vorgabeGraph) {
      setGraph(vorgabeGraph);
      setGraphEpoche((n) => n + 1);
      setError(false);
      return;
    }
    const seq = ++fetchSeqRef.current;
    setGraph(null);
    setError(false);
    setSelected(null);
    fetchCompanyNetworkGraph(companyId)
      .then((g) => {
        if (fetchSeqRef.current !== seq) return;
        setGraph(g);
        setGraphEpoche((n) => n + 1);
      })
      .catch(() => {
        if (fetchSeqRef.current === seq) setError(true);
      });
    return () => {
      // Unmount/Wechsel: alle laufenden Antworten (auch aus refresh) entwerten.
      fetchSeqRef.current++;
    };
  }, [companyId, vorgabeGraph]);

  // Nach einer gespeicherten Änderung: Graph neu laden (Kaskaden sichtbar
  // machen), Detailseite benachrichtigen. Der alte Stand bleibt bis zur
  // Antwort stehen — kein Flackern über den Lade-Zustand.
  const refresh = React.useCallback(() => {
    const seq = ++fetchSeqRef.current;
    fetchCompanyNetworkGraph(companyId)
      .then((g) => {
        if (fetchSeqRef.current !== seq) return;
        setGraph(g);
        // Auswahl abgleichen: fällt der gewählte Knoten aus dem Verbund
        // (z. B. seine einzige Verbindung wurde gerade entfernt), darf keine
        // „Geister-Auswahl" bleiben — die würde alles dimmen, ohne Karte.
        setSelected((prev) =>
          prev != null && g.nodes.some((n) => n.id === prev) ? prev : null,
        );
      })
      .catch(() => {
        /* alten Stand behalten */
      });
    onChanged?.();
  }, [companyId, onChanged]);

  const edges = graph?.edges ?? [];
  const nodeMap = React.useMemo(
    () => new Map((graph?.nodes ?? []).map((n) => [n.id, n])),
    [graph],
  );
  // Bewusst je Render neu (Positionen mutieren in nodesRef; N ist klein).
  const simById = new Map<number, SimNode>(nodesRef.current.map((n) => [n.id, n]));

  // Baum-Layout der Hierarchie-Ansicht (rein aus dem Graphen, keine Simulation).
  const tree = React.useMemo(
    () =>
      graph && mode === "hierarchie"
        ? treeLayout(graph.nodes, graph.edges, verbundAnsicht)
        : null,
    [graph, mode, verbundAnsicht],
  );

  // Simulation (nur Netzwerk-Ansicht): Abstoßung, Federn, Hierarchie-Bias, Zentrierung.
  const step = React.useCallback(() => {
    const ns = nodesRef.current;
    const es = edges;
    const alpha = alphaRef.current;
    const REP = 23000;
    const K = 0.05;
    const CENTER = 0.02;
    const DAMP = 0.85;

    for (let i = 0; i < ns.length; i++) {
      for (let j = i + 1; j < ns.length; j++) {
        const a = ns[i];
        const b = ns[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 1) {
          // Deterministisch minimal auseinanderschieben (identische Startpunkte).
          dx = 0.5 + (i % 3) * 0.25;
          dy = 0.5 + (j % 3) * 0.25;
          d2 = dx * dx + dy * dy;
        }
        d2 = Math.max(d2, 900);
        const d = Math.sqrt(d2);
        const f = (REP * alpha) / d2;
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        a.vx -= fx;
        a.vy -= fy;
        b.vx += fx;
        b.vy += fy;
      }
    }

    const m = new Map<number, SimNode>();
    for (const n of ns) m.set(n.id, n);
    for (const e of es) {
      const a = m.get(e.srcId);
      const b = m.get(e.destId);
      if (!a || !b) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const rest = a.typ === "Firma" && b.typ === "Firma" ? 190 : 130;
      const f = K * (d - rest) * alpha;
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
      // Hierarchie: Mutter (src) über der Tochter (dest) halten.
      if (e.relationType === "Tochterfirma" && b.y - a.y < 70) {
        a.vy -= 1.1 * alpha;
        b.vy += 1.1 * alpha;
      }
    }

    for (const n of ns) {
      if (n.pinned) {
        n.vx = 0;
        n.vy = 0;
        continue;
      }
      n.vx += (NET_W / 2 - n.x) * CENTER * alpha;
      n.vy += (NET_H / 2 - n.y) * CENTER * alpha;
      n.vx *= DAMP;
      n.vy *= DAMP;
      n.x = Math.min(NET_W - 60, Math.max(60, n.x + n.vx));
      n.y = Math.min(NET_H - 70, Math.max(46, n.y + n.vy));
    }

    alphaRef.current = alpha * 0.985;
    setTick((t) => t + 1);
    if (alphaRef.current > 0.02) {
      rafRef.current = window.requestAnimationFrame(step);
    } else {
      rafRef.current = null;
    }
  }, [edges]);

  const kickSim = React.useCallback(
    (minAlpha: number) => {
      alphaRef.current = Math.max(alphaRef.current, minAlpha);
      if (rafRef.current == null) {
        rafRef.current = window.requestAnimationFrame(step);
      }
    },
    [step],
  );

  // Netzwerk-Ansicht: Ring-Startpositionen + Simulation starten. Die Baum-
  // Ansicht braucht keine Positions-Initialisierung (Layout im useMemo) —
  // nur eine evtl. laufende Simulation stoppen.
  React.useEffect(() => {
    if (!graph) return;
    if (mode === "hierarchie") {
      alphaRef.current = 0;
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    const firmen = graph.nodes.filter((n) => n.typ === "Firma" && !n.isSelf);
    const persons = graph.nodes.filter((n) => n.typ === "Person");
    nodesRef.current = graph.nodes.map((n): SimNode => {
      if (n.isSelf) return { ...n, x: NET_W / 2, y: NET_H / 2, vx: 0, vy: 0, pinned: false };
      const ring = n.typ === "Firma" ? firmen : persons;
      const i = ring.findIndex((r) => r.id === n.id);
      const count = Math.max(ring.length, 1);
      const ang = (2 * Math.PI * i) / count + (n.typ === "Person" ? 0.6 : 0.15);
      const rad = n.typ === "Firma" ? 195 : 285;
      return {
        ...n,
        x: NET_W / 2 + rad * Math.cos(ang),
        y: NET_H / 2 + rad * Math.sin(ang) * 0.78,
        vx: 0,
        vy: 0,
        pinned: false,
      };
    });
    alphaRef.current = 1;
    if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [graph, mode, step]);

  // Client-Koordinaten → Zeichenfläche, über dieselbe Kamera, aus der die
  // viewBox entsteht (die Refs tragen den zuletzt gezeichneten Stand).
  const kameraRef = React.useRef<Kamera>({ zoom: 1, cx: 0, cy: 0 });
  const panelRef = React.useRef<Groesse>(PANEL_ERSATZ);
  const inhaltRef = React.useRef<Rahmen>({ x: 0, y: 0, w: NET_W, h: NET_H });
  const svgPoint = React.useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    return panelZuWelt(
      { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) },
      kameraRef.current,
      panelRef.current,
    );
  }, []);

  // Drag eines Knotens (nur Netzwerk-Ansicht); < 5px Bewegung beim Loslassen
  // zählt als Klick (Auswahl).
  const startDrag = React.useCallback(
    (nodeId: number, downEvent: React.PointerEvent) => {
      downEvent.preventDefault();
      downEvent.stopPropagation();
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      const start = { x: downEvent.clientX, y: downEvent.clientY };
      let moved = 0;
      const onMove = (e: PointerEvent) => {
        moved = Math.max(moved, Math.hypot(e.clientX - start.x, e.clientY - start.y));
        if (moved < 3) return;
        const p = svgPoint(e.clientX, e.clientY);
        node.x = Math.min(NET_W - 60, Math.max(60, p.x));
        node.y = Math.min(NET_H - 70, Math.max(46, p.y));
        node.pinned = true;
        kickSim(0.3);
      };
      const detach = () => {
        alleFenster.removeEventListener("pointermove", onMove);
        alleFenster.removeEventListener("pointerup", onUp);
        dragCleanupRef.current = null;
      };
      const onUp = () => {
        detach();
        if (moved < 5) {
          setSelected((prev) => (prev === nodeId ? null : nodeId));
        }
      };
      dragCleanupRef.current?.();
      dragCleanupRef.current = detach;
      alleFenster.addEventListener("pointermove", onMove);
      alleFenster.addEventListener("pointerup", onUp);
    },
    [kickSim, svgPoint],
  );

  const selNode = selected != null ? nodeMap.get(selected) ?? null : null;
  const selEdges = React.useMemo(() => {
    if (selected == null) return [];
    return edges.filter((e) => e.srcId === selected || e.destId === selected);
  }, [edges, selected]);

  const edgeStyle = (e: OrgGraphEdge, highlighted: boolean) => {
    const isPersonEdge = !COMPANY_EDGE_TYPES.has(e.relationType);
    const istVerwaltung = e.relationType === VERWALTUNG_EDGE;
    const istBruecke = e.relationType === PERSONEN_BRUECKE_EDGE;
    if (istBruecke) {
      // Gerechnete Kante „über eine gemeinsame Person": in der Personenfarbe,
      // aber gestrichelt — sie ist keine gespeicherte Beziehung.
      return {
        stroke: highlighted ? "#f0a020" : "#a898a4",
        strokeWidth: highlighted ? 3 : 1.6,
        strokeDasharray: "6 4",
        opacity: selected != null && !highlighted ? 0.35 : 0.85,
      } as const;
    }
    return {
      // Die Verwaltungskante ist keine Beteiligung — sie darf nicht aussehen
      // wie eine. Eigene Strichelung, damit sie sich auch von der
      // Schwester-Kante unterscheidet.
      stroke: highlighted ? "#f0a020" : istVerwaltung ? "#7f93a8" : isPersonEdge ? "#c2b4bd" : "#a2586a",
      strokeWidth: highlighted ? 3 : istVerwaltung ? 1.8 : isPersonEdge ? 1.4 : 2,
      strokeDasharray: istVerwaltung
        ? "5 4"
        : e.relationType === "Schwester"
          ? "2 7"
          : e.relationType === "Beteiligung"
            ? "9 6"
            : undefined,
      opacity: selected != null && !highlighted ? 0.35 : 0.85,
    } as const;
  };

  // Stift der Info-Karte: bestehende Firma↔Firma-Strukturkante im
  // RelationCreateDialog bearbeiten — aus der Perspektive des gewählten
  // Knotens, exakt wie der Stift in der Tabelle „Verbindungen zu Firmen".
  // Struktur-Kanten behalten im Graph-Endpoint die DB-Richtung, edge.id ist
  // die echte ContactRelation.id — beides Voraussetzung für PATCH/Kippen.
  const openEdgeEdit = React.useCallback(
    (self: OrgGraphNode, e: OrgGraphEdge) => {
      const otherId = e.srcId === self.id ? e.destId : e.srcId;
      const other = nodeMap.get(otherId);
      setActionError("");
      setRelDialog({
        forId: self.id,
        forName: self.name ?? "",
        edit: {
          relationId: e.id,
          srcId: e.srcId,
          destId: e.destId,
          other: { id: otherId, name: other?.name ?? "—", type: "Firma" },
          relType: edgeLabelFor(self.id, e),
          rawRelType: e.relationType,
          share: e.share ?? undefined,
          isExtern: false,
          sameHousehold: false,
        },
      });
    },
    [nodeMap],
  );

  // Papierkorb: genau DIESE Kante löschen (Backend räumt verwaiste
  // Schwester-Kanten einer entfernten Tochterfirma-Beziehung selbst auf).
  const doDelete = React.useCallback(async () => {
    if (!confirmDel) return;
    setDelBusy(true);
    try {
      await apiFetch(`contact-relation/${confirmDel.edge.id}`, { method: "DELETE" });
      // Steht dieselbe Beteiligung als zweite Zeile in den Daten (Tochterfirma
      // UND Gesellschafter), geht sie mit — sonst tauchte nach dem Entfernen
      // die andere Zeile als „neue" Verbindung derselben Firmen auf.
      for (const id of confirmDel.edge.zusammengefasst ?? []) {
        await apiFetch(`contact-relation/${id}`, { method: "DELETE" }).catch(() => undefined);
      }
      setConfirmDel(null);
      refresh();
    } catch {
      setConfirmDel(null);
      setActionError("Verbindung konnte nicht entfernt werden.");
    } finally {
      setDelBusy(false);
    }
  }, [confirmDel, refresh]);

  /**
   * Inhalt eines Kästchens in der Verbund-Ansicht: Name und Anschrift oben,
   * darunter — durch eine Linie getrennt — Unternehmensfunktion, Branche und
   * Tätigkeit, jeweils mit ihrem Bildzeichen.
   *
   * Genau diese vier Angaben entscheiden, welches Risiko bei welcher Firma des
   * Verbunds liegt. Fehlt eine, steht „nicht erfasst" da statt einer leeren
   * Zeile: die Lücke ist die Aussage.
   *
   * Der Text wird hart abgeschnitten — SVG bricht nicht um, und ein über den
   * Rahmen hinauslaufender Name liefe in das Nachbarkästchen. Der volle Wert
   * steht im <title> (Tooltip) und in der Info-Karte darunter.
   */
  const detailInhalt = (n: OrgGraphNode, w: number, h: number) => {
    const links = -w / 2 + 12;
    const oben = -h / 2;
    const funktionen = funktionenAus(n.unternehmensfunktion);
    const branchen = branchenAus(brancheRows, n.brancheClassId, n.weitereBranchen);
    const taetigkeit = (n.taetigkeit ?? "").trim();

    /** Eine Merkmalszeile: Bildzeichen links, Text daneben. */
    const zeile = (
      key: string,
      y: number,
      icon: JSX.Element | null,
      text: string,
      max: number,
    ) => (
      <g key={key}>
        {icon && (
          <svg
            x={links}
            y={y - 9}
            width={12}
            height={12}
            viewBox={ICON_VIEWBOX}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="org-tbox-ico"
          >
            {icon}
          </svg>
        )}
        <text
          x={links + (icon ? 18 : 0)}
          y={y}
          className={text ? "org-tbox-zeile" : "org-tbox-zeile leer"}
        >
          {text ? truncate(text, max) : "nicht erfasst"}
        </text>
      </g>
    );

    // Verwaltet diese Firma Gebäude des Verbunds, steht das als Marke oben
    // rechts — im Immobilienverbund ist das die Stelle, an der die Objekte
    // betreut werden, und die gehört ins Kästchen, nicht in eine Legende.
    const istVerwalter = verwalterIds.has(n.id);

    return (
      <>
        <text
          x={links}
          y={oben + 20}
          className={"org-tbox-name" + (n.isSelf ? " self" : "")}
        >
          {truncate(n.name || "—", istVerwalter ? 20 : 30)}
        </text>
        {istVerwalter && (
          <g>
            <rect
              x={w / 2 - 12 - 86}
              y={oben + 6}
              width={86}
              height={16}
              rx={8}
              className="org-tbox-marke"
            />
            <text x={w / 2 - 12 - 43} y={oben + 17} className="org-tbox-marke-text">
              Hausverwaltung
            </text>
          </g>
        )}
        <text x={links} y={oben + 34} className="org-tbox-anschrift">
          {truncate((n.anschrift ?? "").trim() || "Anschrift nicht erfasst", 38)}
        </text>
        <line
          x1={links}
          y1={oben + 43}
          x2={w / 2 - 12}
          y2={oben + 43}
          className="org-tbox-trenner"
        />
        {zeile(
          "funktion",
          oben + 62,
          unternehmensfunktionIcon(funktionen[0]),
          funktionen.join(" · "),
          34,
        )}
        {zeile(
          "branche",
          oben + 80,
          brancheIcon(branchen[0]?.ebene1),
          branchen.map((b) => b.pfad).join(" · "),
          36,
        )}
        {zeile("taetigkeit", oben + 98, null, taetigkeit, 42)}
      </>
    );
  };

  /* ─── Zoom & Ausschnitt ─────────────────────────────────────────────────
     Die Kamera (Mittelpunkt + Zoom) und die gemessene Panelgröße ergeben die
     viewBox. Gezeichnet wird immer die begrenzte Kamera — so bleibt nach einer
     Größenänderung oder einem neu geladenen Graphen etwas im Bild. */
  React.useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([eintrag]) => {
      const { width, height } = eintrag.contentRect;
      if (width > 0 && height > 0) {
        setPanelGemessen((alt) =>
          alt && Math.abs(alt.w - width) < 0.5 && Math.abs(alt.h - height) < 0.5
            ? alt
            : { w: width, h: height },
        );
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const panel = panelGemessen ?? PANEL_ERSATZ;

  const inhaltsRahmen: Rahmen = React.useMemo(() => {
    if (mode === "hierarchie" && tree && graph) {
      const boxen = graph.nodes.flatMap((n) => {
        const p = tree.pos.get(n.id);
        return p ? [{ x: p.x, y: p.y, w: boxW(n, verbundAnsicht), h: boxH(n, verbundAnsicht) }] : [];
      });
      return rahmenUm(boxen) ?? { x: 0, y: 0, w: tree.w, h: tree.h };
    }
    return { x: 0, y: 0, w: NET_W, h: NET_H };
  }, [mode, tree, graph, verbundAnsicht]);

  // Die eigene Firma — auf sie zentriert die Startansicht, wenn nicht alles passt.
  const fokus: Punkt | null = React.useMemo(() => {
    if (mode !== "hierarchie" || !tree || !graph) return { x: NET_W / 2, y: NET_H / 2 };
    const selbst = graph.nodes.find((n) => n.isSelf)?.id ?? companyId;
    return tree.pos.get(selbst) ?? null;
  }, [mode, tree, graph, companyId]);

  // Neuer Graph, andere Ansicht, Personen ein/aus oder erstmals gemessen:
  // die Wahl des Users verwerfen, die Startansicht gilt wieder.
  const startSchluessel = `${graphEpoche}|${mode}|${personenZeigen}|${panelGemessen ? 1 : 0}`;
  const [startFuer, setStartFuer] = React.useState(startSchluessel);
  if (startFuer !== startSchluessel) {
    setStartFuer(startSchluessel);
    setKameraWahl(null);
  }

  const kamera: Kamera = kameraWahl
    ? kameraBegrenzen(kameraWahl, inhaltsRahmen, panel)
    : startKamera(inhaltsRahmen, panel, fokus);
  kameraRef.current = kamera;
  panelRef.current = panel;
  inhaltRef.current = inhaltsRahmen;
  const vb = viewBoxVon(kamera, panel);

  /** Neue Kamera setzen, begrenzt auf „etwas vom Graphen bleibt sichtbar". */
  const kameraSetzen = React.useCallback((naechste: (k: Kamera) => Kamera) => {
    setKameraWahl(kameraBegrenzen(naechste(kameraRef.current), inhaltRef.current, panelRef.current));
  }, []);

  // Strg/Cmd + Rad zoomt um den Cursor. Nativ und nicht passiv: Reacts
  // onWheel ist passiv, `preventDefault` liefe dort ins Leere und der Browser
  // zoomte die ganze Seite mit. Ohne Strg bleibt das Rad beim Scrollen der Seite.
  const svgDa = !!graph && !error;
  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svgDa || !svg) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      const um = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      kameraSetzen((k) => zoomen(k, k.zoom * radFaktor(e.deltaY, e.deltaMode), panelRef.current, um));
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [svgDa, kameraSetzen]);

  /**
   * Ziehen auf leerer Fläche verschiebt den Ausschnitt — in jeder Zoomstufe.
   * Ein Klick ohne Bewegung hebt die Auswahl auf.
   */
  const startPan = React.useCallback(
    (downEvent: React.PointerEvent<SVGSVGElement>) => {
      if (downEvent.target !== downEvent.currentTarget) return;
      const start = { x: downEvent.clientX, y: downEvent.clientY };
      const startKam = kameraRef.current;
      let moved = 0;
      const onMove = (e: PointerEvent) => {
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        moved = Math.max(moved, Math.hypot(dx, dy));
        if (moved < 3) return;
        kameraSetzen(() => verschieben(startKam, dx, dy));
      };
      const detach = () => {
        alleFenster.removeEventListener("pointermove", onMove);
        alleFenster.removeEventListener("pointerup", onUp);
        dragCleanupRef.current = null;
      };
      const onUp = () => {
        detach();
        if (moved < 4) setSelected(null);
      };
      dragCleanupRef.current?.();
      dragCleanupRef.current = detach;
      alleFenster.addEventListener("pointermove", onMove);
      alleFenster.addEventListener("pointerup", onUp);
    },
    [kameraSetzen],
  );

  const inhalt = (
    <div
      className={isInline ? "kvp-rd org-inline" : "kvp-rd org-overlay"}
      role={isInline ? undefined : "dialog"}
      aria-modal={isInline ? undefined : true}
      aria-label="Organigramm"
      onMouseDown={
        isInline
          ? undefined
          : (e) => {
              if (e.target === e.currentTarget) onClose?.();
            }
      }
    >
      <div className="org-panel">
        <div className="org-head">
          <div>
            <div className="org-kicker">Organigramm</div>
            <h3 className="org-title">{companyName || "Unternehmensverbund"}</h3>
          </div>
          <div className="org-head-right">
            {!verbundAnsicht && (
            <div className="org-mode" role="tablist" aria-label="Ansicht">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "hierarchie"}
                className={mode === "hierarchie" ? "on" : ""}
                onClick={() => setMode("hierarchie")}
              >
                Hierarchie
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "netzwerk"}
                className={mode === "netzwerk" ? "on" : ""}
                onClick={() => setMode("netzwerk")}
              >
                Netzwerk
              </button>
            </div>
            )}
            {!verbundAnsicht && (
              <div className="org-mode org-personen">
                <button
                  type="button"
                  aria-pressed={personenZeigen}
                  className={personenZeigen ? "on" : ""}
                  title={
                    personenZeigen
                      ? "Personen ausblenden — Firmen, die nur über eine Person verbunden sind, bleiben mit einer gestrichelten Linie sichtbar"
                      : "Personen wieder einblenden"
                  }
                  onClick={() => setPersonenZeigen(!personenZeigen)}
                >
                  Personen anzeigen
                </button>
              </div>
            )}
            <div className="org-zoom" role="group" aria-label="Zoom">
              <button
                type="button"
                aria-label="Verkleinern"
                disabled={kamera.zoom <= ZOOM_MIN + 0.001}
                onClick={() => kameraSetzen((k) => zoomen(k, zoomStufe(k.zoom, -1), panelRef.current))}
              >
                −
              </button>
              <span className="org-zoom-val" aria-label="Zoomstufe">{Math.round(kamera.zoom * 100)} %</span>
              <button
                type="button"
                aria-label="Vergrößern"
                disabled={kamera.zoom >= ZOOM_MAX - 0.001}
                onClick={() => kameraSetzen((k) => zoomen(k, zoomStufe(k.zoom, 1), panelRef.current))}
              >
                +
              </button>
              <button
                type="button"
                className="org-zoom-fit"
                title="Den ganzen Graphen ins Bild holen"
                onClick={() => kameraSetzen(() => eingepassteKamera(inhaltRef.current, panelRef.current))}
              >
                Einpassen
              </button>
              <button
                type="button"
                className="org-zoom-fit"
                title="Echte Größe"
                disabled={Math.abs(kamera.zoom - 1) < 0.001}
                onClick={() => kameraSetzen((k) => zoomen(k, 1, panelRef.current))}
              >
                100 %
              </button>
            </div>
            {!isInline && (
              <button type="button" className="org-x" aria-label="Schließen" onClick={onClose}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15" /></svg>
              </button>
            )}
          </div>
        </div>
        <div className="org-body" ref={bodyRef}>
          {error ? (
            <div className="org-msg">Organigramm konnte nicht geladen werden.</div>
          ) : !graph ? (
            <div className="org-msg">Lädt Netzwerk …</div>
          ) : (
            <>
              <svg
                ref={svgRef}
                className="org-svg"
                viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ cursor: "grab" }}
                onPointerDown={startPan}
              >
                <defs>
                  <marker id="orgArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0 0L10 5L0 10z" fill="#a2586a" />
                  </marker>
                  <marker id="orgArrowSel" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0 0L10 5L0 10z" fill="#f0a020" />
                  </marker>
                </defs>

                {mode === "hierarchie" && tree ? (
                  <>
                    {/* Querbögen (Schwester/Beteiligung, Zweit-Eltern) unter dem Baum */}
                    {tree.cross.map((e) => {
                      const na = nodeMap.get(e.srcId);
                      const nb = nodeMap.get(e.destId);
                      const a = tree.pos.get(e.srcId);
                      const b = tree.pos.get(e.destId);
                      if (!na || !nb || !a || !b) return null;
                      const p1 = rectAnchor(a, na, b, verbundAnsicht);
                      const p2 = rectAnchor(b, nb, a, verbundAnsicht);
                      const dx = p2.x - p1.x;
                      const dy = p2.y - p1.y;
                      const dl = Math.max(Math.hypot(dx, dy), 1);
                      const bow = Math.min(56, dl * 0.22);
                      const cx = (p1.x + p2.x) / 2 - (dy / dl) * bow;
                      const cy = (p1.y + p2.y) / 2 + (dx / dl) * bow;
                      const highlighted =
                        selected != null && (e.srcId === selected || e.destId === selected);
                      const st = edgeStyle(e, highlighted);
                      return (
                        <g key={e.id}>
                          <path
                            d={`M ${p1.x} ${p1.y} Q ${cx} ${cy} ${p2.x} ${p2.y}`}
                            fill="none"
                            stroke={st.stroke}
                            strokeWidth={st.strokeWidth}
                            strokeDasharray={st.strokeDasharray}
                            opacity={st.opacity}
                            markerEnd={
                              e.relationType === "Tochterfirma"
                                ? `url(#${highlighted ? "orgArrowSel" : "orgArrow"})`
                                : undefined
                            }
                          />
                          {highlighted && (
                            <text x={cx} y={cy - 5} className="org-edge-label" textAnchor="middle">
                              {hoverLabel(e)}
                            </text>
                          )}
                        </g>
                      );
                    })}
                    {/* Baum-Bögen: Unterkante Elternteil → Oberkante Kind */}
                    {Array.from(tree.primary, ([childId, pr]) => {
                      const parent = nodeMap.get(pr.p);
                      const child = nodeMap.get(childId);
                      const a = tree.pos.get(pr.p);
                      const b = tree.pos.get(childId);
                      if (!parent || !child || !a || !b) return null;
                      const e = pr.e;
                      const isTochter = e.relationType === "Tochterfirma";
                      const x1 = a.x;
                      const y1 = a.y + boxH(parent, verbundAnsicht) / 2;
                      const x2 = b.x;
                      const y2 = b.y - boxH(child, verbundAnsicht) / 2 - (isTochter ? 5 : 0);
                      const midY = (y1 + y2) / 2;
                      const highlighted =
                        selected != null && (e.srcId === selected || e.destId === selected);
                      const st = edgeStyle(e, highlighted);
                      const label = treeEdgeLabel(e);
                      return (
                        <g key={e.id}>
                          <path
                            d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                            fill="none"
                            stroke={st.stroke}
                            strokeWidth={st.strokeWidth}
                            opacity={st.opacity}
                            markerEnd={
                              isTochter
                                ? `url(#${highlighted ? "orgArrowSel" : "orgArrow"})`
                                : undefined
                            }
                          />
                          {label && (
                            <text
                              x={(x1 + x2) / 2}
                              y={midY - 5}
                              className="org-edge-label tree"
                              textAnchor="middle"
                            >
                              {label}
                            </text>
                          )}
                        </g>
                      );
                    })}
                    {/* Kästchen */}
                    {graph.nodes.map((n) => {
                      const p = tree.pos.get(n.id);
                      if (!p) return null;
                      const w = boxW(n, verbundAnsicht);
                      const h = boxH(n, verbundAnsicht);
                      const isSel = selected === n.id;
                      const dim =
                        selected != null &&
                        !isSel &&
                        !selEdges.some((e) => e.srcId === n.id || e.destId === n.id);
                      return (
                        <g
                          key={n.id}
                          transform={`translate(${p.x},${p.y})`}
                          className="org-tbox"
                          opacity={dim ? 0.4 : 1}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setSelected((prev) => (prev === n.id ? null : n.id));
                          }}
                        >
                          <title>
                            {verbundAnsicht
                              ? [
                                  n.name || "—",
                                  (n.anschrift ?? "").trim(),
                                  funktionenAus(n.unternehmensfunktion).join(" · "),
                                  branchenAus(brancheRows, n.brancheClassId, n.weitereBranchen)
                                    .map((b) => b.pfad)
                                    .join(" · "),
                                  (n.taetigkeit ?? "").trim(),
                                ]
                                  .filter(Boolean)
                                  .join("\n")
                              : n.name || "—"}
                          </title>
                          <rect
                            x={-w / 2}
                            y={-h / 2}
                            width={w}
                            height={h}
                            rx={10}
                            fill={n.isSelf ? "#fdf4fa" : "#fff"}
                            stroke={
                              isSel
                                ? "#f0a020"
                                : n.isSelf
                                  ? "#7d2169"
                                  : n.typ === "Firma"
                                    ? "#c8a3ac"
                                    : "#d5cdd4"
                            }
                            strokeWidth={isSel ? 3 : n.isSelf ? 2.2 : 1.5}
                          />
                          {verbundAnsicht ? (
                            detailInhalt(n, w, h)
                          ) : (
                            <>
                              <circle cx={-w / 2 + 20} cy={0} r={12} fill={nodeFill(n)} />
                              <g transform={`translate(${-w / 2 + 20},0) scale(0.62)`}>
                                {n.typ === "Firma" ? BuildingGlyph : PersonGlyph}
                              </g>
                              <text
                                x={-w / 2 + 38}
                                y={4.5}
                                className={"org-tbox-name" + (n.isSelf ? " self" : "")}
                              >
                                {truncate(n.name || "—", n.typ === "Firma" ? 28 : 24)}
                              </text>
                            </>
                          )}
                        </g>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {edges.map((e) => {
                      const a = simById.get(e.srcId);
                      const b = simById.get(e.destId);
                      if (!a || !b) return null;
                      const dx = b.x - a.x;
                      const dy = b.y - a.y;
                      const d = Math.max(Math.hypot(dx, dy), 1);
                      const ux = dx / d;
                      const uy = dy / d;
                      const ra = nodeRadius(a) + 3;
                      const rb = nodeRadius(b) + (e.relationType === "Tochterfirma" ? 9 : 3);
                      const x1 = a.x + ux * ra;
                      const y1 = a.y + uy * ra;
                      const x2 = b.x - ux * rb;
                      const y2 = b.y - uy * rb;
                      const highlighted =
                        selected != null && (e.srcId === selected || e.destId === selected);
                      const st = edgeStyle(e, highlighted);
                      return (
                        <g key={e.id}>
                          <line
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke={st.stroke}
                            strokeWidth={st.strokeWidth}
                            strokeDasharray={st.strokeDasharray}
                            opacity={st.opacity}
                            markerEnd={
                              e.relationType === "Tochterfirma"
                                ? `url(#${highlighted ? "orgArrowSel" : "orgArrow"})`
                                : undefined
                            }
                          />
                          {highlighted && (
                            <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 6} className="org-edge-label" textAnchor="middle">
                              {hoverLabel(e)}
                            </text>
                          )}
                        </g>
                      );
                    })}
                    {nodesRef.current.map((n) => {
                      const r = nodeRadius(n);
                      const isSel = selected === n.id;
                      const dim =
                        selected != null &&
                        !isSel &&
                        !selEdges.some((e) => e.srcId === n.id || e.destId === n.id);
                      return (
                        <g
                          key={n.id}
                          transform={`translate(${n.x},${n.y})`}
                          className="org-node"
                          opacity={dim ? 0.45 : 1}
                          onPointerDown={(e) => startDrag(n.id, e)}
                        >
                          <circle r={r} fill={nodeFill(n)} stroke={isSel ? "#f0a020" : "#fff"} strokeWidth={isSel ? 3.5 : 2} />
                          <g transform={`scale(${r / 26})`}>{n.typ === "Firma" ? BuildingGlyph : PersonGlyph}</g>
                          <text y={r + 16} textAnchor="middle" className={"org-label" + (n.isSelf ? " self" : "")}>
                            {truncate(n.name || "—")}
                          </text>
                        </g>
                      );
                    })}
                  </>
                )}
              </svg>

              <div className="org-legend">
                <div><svg width="34" height="8"><line x1="1" y1="4" x2="26" y2="4" stroke="#a2586a" strokeWidth="2" markerEnd="url(#orgArrow)" /></svg> Tochterfirma (Pfeil zur Tochter)</div>
                <div><svg width="34" height="8"><line x1="1" y1="4" x2="33" y2="4" stroke="#a2586a" strokeWidth="2" strokeDasharray="2 7" /></svg> Schwester</div>
                <div><svg width="34" height="8"><line x1="1" y1="4" x2="33" y2="4" stroke="#a2586a" strokeWidth="2" strokeDasharray="9 6" /></svg> Beteiligung</div>
                {personenZeigen ? (
                  <div><svg width="34" height="8"><line x1="1" y1="4" x2="33" y2="4" stroke="#c2b4bd" strokeWidth="1.4" /></svg> Person (GF, Gesellschafter …)</div>
                ) : (
                  <div><svg width="34" height="8"><line x1="1" y1="4" x2="33" y2="4" stroke="#a898a4" strokeWidth="1.6" strokeDasharray="6 4" /></svg> über gemeinsame Person</div>
                )}
              </div>

              {!selNode && (
                <div className="org-hint">
                  {mode === "hierarchie"
                    ? canWrite
                      ? "Klick auf ein Kästchen: Beziehungen anzeigen und bearbeiten"
                      : "Klick auf ein Kästchen zeigt die Beziehungen"
                    : "Knoten ziehen zum Anordnen · Klick zeigt die Beziehungen"}
                  {" · Fläche ziehen verschiebt · Strg + Mausrad zoomt"}
                </div>
              )}

              {selNode && (
                <div className="org-card">
                  <div className="org-card-title">{selNode.name || "—"}</div>
                  <div className="org-card-list">
                    {selEdges.length === 0 && <div className="org-card-row muted">Keine Verbindungen.</div>}
                    {selEdges.map((e) => {
                      const otherId = e.srcId === selNode.id ? e.destId : e.srcId;
                      const other = nodeMap.get(otherId);
                      if (!other) return null;
                      // Bearbeiten/Entfernen nur für Firma↔Firma-Strukturkanten —
                      // Personen-Rollen werden unter „Menschen fürs Unternehmen"
                      // gepflegt (und kommen hier richtungs-normalisiert an).
                      const editableEdge =
                        canWrite &&
                        selNode.typ === "Firma" &&
                        e.id > 0 && // gerechnete Kanten (Brücke, Verwaltung) sind nicht gespeichert
                        COMPANY_EDGE_TYPES.has(e.relationType);
                      return (
                        <div key={e.id} className="org-card-row">
                          <span className="org-card-top">
                            <span className="org-card-name">{other.name || "—"}</span>
                            {editableEdge && (
                              <span className="org-card-act">
                                <button
                                  type="button"
                                  className="rel-edit"
                                  title="Beziehung bearbeiten"
                                  onClick={() => openEdgeEdit(selNode, e)}
                                >
                                  {PenIcon}
                                </button>
                                <button
                                  type="button"
                                  className="rel-del"
                                  title="Verbindung entfernen"
                                  onClick={() => {
                                    setActionError("");
                                    setConfirmDel({ edge: e, otherName: other.name || "—" });
                                  }}
                                >
                                  {DelIcon}
                                </button>
                              </span>
                            )}
                          </span>
                          <span className="org-card-rel">
                            {edgeLabelFor(selNode.id, e)}
                            {kantenAnteil(e) ? ` · Anteile ${kantenAnteil(e)}` : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {actionError && <div className="org-err">{actionError}</div>}
                  {canWrite && selNode.typ === "Firma" && (
                    <button
                      type="button"
                      className="org-open org-add"
                      onClick={() => {
                        setActionError("");
                        setRelDialog({ forId: selNode.id, forName: selNode.name ?? "" });
                      }}
                    >
                      Firma verbinden
                    </button>
                  )}
                  {/* In der Anlage (vorgabeGraph) ginge beim Wegnavigieren die
                      ungespeicherte Eingabe verloren. */}
                  {!selNode.isSelf && !vorgabeGraph && (
                    <button
                      type="button"
                      className="org-open"
                      onClick={() => {
                        onClose?.();
                        navigate(`/contacts/${selNode.id}`);
                      }}
                    >
                      Kontakt öffnen
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {relDialog && (
        <RelationCreateDialog
          contactId={relDialog.forId}
          kind="firmaConn"
          selfName={relDialog.forName}
          editRelation={relDialog.edit}
          onClose={() => setRelDialog(null)}
          onSaved={refresh}
        />
      )}
      <ConfirmDialog
        open={confirmDel != null}
        title="Verbindung entfernen"
        message={
          confirmDel ? (
            <>
              Die Verbindung zu <b>{confirmDel.otherName}</b> wirklich entfernen? Der Kontakt
              selbst bleibt bestehen.
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Entfernen"
        busy={delBusy}
        onConfirm={doDelete}
        onCancel={() => {
          if (!delBusy) setConfirmDel(null);
        }}
      />
    </div>
  );
  // Das Popup hängt per Portal an document.body: In der gesperrten Akte liegt
  // der Auslöser in einem `<fieldset disabled>` — gerendert an Ort und Stelle
  // waren dort auch „Schließen" und die Ansichts-Knöpfe des Popups stumm, es
  // ließ sich nicht mehr schließen (PROD-Befund 28.09.2026). Die eingebettete
  // Variante (Risiken-Reiter) bleibt, wo sie ist.
  return isInline ? inhalt : createPortal(inhalt, document.body);
}
